// Copyright 2021 Sorama B.V.

import { computedFrom } from "aurelia-framework";
import { IPagedRequest } from "./IWebRequestHandler";

export class TransformedPagedRequest<TDest, TSource> implements IPagedRequest<TDest> {
    private _finished: boolean;
    public readonly total: TDest[] = [];

    @computedFrom("_finished")
    public get finished(): boolean {
        return this._finished;
    }

    private queuePromise: Promise<unknown>;

    private constructor(
        private readonly source: IPagedRequest<TSource>,
        private readonly transformer: (x: TSource) => Promise<TDest> | TDest
    ) {
        this.queuePromise = this.syncToSource();
    }

    public next(): Promise<TDest[]> {
        const promise = this.queuePromise ? this.queuePromise : Promise.resolve();
        const nextPromise = promise.then(async () => {
            // Fetch next values
            await this.source.next();

            return await this.syncToSource();
        });

        this.queuePromise = nextPromise.catch(() => {});
        return nextPromise;
    }

    public readToEnd(): Promise<TDest[]> {
        const promise = this.queuePromise ? this.queuePromise : Promise.resolve();
        const nextPromise = promise.then(async () => {
            // Fetch next values
            await this.source.readToEnd();
            await this.syncToSource();

            return this.total;
        });

        this.queuePromise = nextPromise.catch(() => {});
        return nextPromise;
    }

    public getCount(): Promise<number> {
        return this.source.getCount();
    }

    private async syncToSource(): Promise<TDest[]> {
        // Now transform all values
        const values = await Promise.all(this.source.total.slice(this.total.length).map((x) => this.transformer(x)));

        // Update total list
        this.total.splice(this.total.length, 0, ...values);

        // Update complete
        this._finished = this.source.finished;

        return values;
    }

    static create<TSource, TDest>(
        source: IPagedRequest<TSource>,
        transformer: (x: TSource) => Promise<TDest> | TDest
    ): IPagedRequest<TDest> {
        return new TransformedPagedRequest(source, transformer);
    }
}
